import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import {useIntl} from 'react-intl'
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";

interface FormData {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
}

interface FormErrors {
  startDate?: string;
  endDate?: string;
}

interface DateRangeModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => void;
  initialData?: FormData;
}

const defaultFormData: FormData = {
  title: '',
  description: '',
  startDate: '',
  endDate: ''
};

const CalendarDatePickerComponent: React.FC<DateRangeModalProps> = ({ 
  show, 
  onHide, 
  onSubmit,
  initialData = defaultFormData
}) => {
  const intl = useIntl();
  const [formData, setFormData] = useState<FormData>(initialData);
  const [errors, setErrors] = useState<FormErrors>({});
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const validateDates = () => {
    const newErrors: FormErrors = {};
    const currentDateTime = new Date();

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    } else if (new Date(formData.startDate) < currentDateTime) {
      newErrors.startDate = "Start date and time cannot be in the past";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    } else if (formData.startDate) {
      if (new Date(formData.endDate) <= new Date(formData.startDate)) {
        newErrors.endDate = "End date and time must be after start date and time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'startDate' || name === 'endDate') {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleDatePartChange = (date: Date[], field: 'startDate' | 'endDate') => {
    if (!date.length) return;
    const selectedDate = date[0];
    
    let current = formData[field] ? new Date(formData[field]) : new Date(selectedDate);
    
    if (formData[field]) {
         current.setFullYear(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    } else {
        current = selectedDate;
    }

    setFormData(prev => ({
      ...prev,
      [field]: current.toISOString()
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };

  const handleTimePartChange = (date: Date[], field: 'startDate' | 'endDate') => {
    if (!date.length) return;
    const selectedTime = date[0];
    
    let current = formData[field] ? new Date(formData[field]) : new Date();
    
    current.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);

    setFormData(prev => ({
      ...prev,
      [field]: current.toISOString()
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateDates()) {
      onSubmit(formData);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({id: 'CALENDAR.DATE.PICKER.MODAL.TITLE'})}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{intl.formatMessage({id: 'CALENDAR.DATE.PICKER.TITLE.FIELD'})}</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder={intl.formatMessage({id: 'CALENDAR.DATE.PICKER.TITLE.FIELD.TITLE.PLACEHOLDER'})}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{intl.formatMessage({id: 'CALENDAR.DATE.PICKER.START.FIELD'})}</Form.Label>
            <Row>
              <Col md={8}>
                <Flatpickr
                  value={formData.startDate}
                  onChange={(date) => handleDatePartChange(date, 'startDate')}
                  options={{
                    enableTime: false,
                    dateFormat: "Y-m-d",
                  }}
                  className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                  placeholder="Select Date"
                />
              </Col>
              <Col md={4}>
                <Flatpickr
                  value={formData.startDate}
                  onChange={(date) => handleTimePartChange(date, 'startDate')}
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: "H:i",
                    time_24hr: true,
                    minuteIncrement: 15,
                  }}
                  className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                  placeholder="Select Time"
                />
              </Col>
            </Row>
            {errors.startDate && (
              <div className="d-block invalid-feedback">
                {errors.startDate}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{intl.formatMessage({id: 'CALENDAR.DATE.PICKER.END.FIELD'})}</Form.Label>
            <Row>
              <Col md={8}>
                <Flatpickr
                  value={formData.endDate}
                  onChange={(date) => handleDatePartChange(date, 'endDate')}
                  options={{
                    enableTime: false,
                    dateFormat: "Y-m-d",
                  }}
                  className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                  placeholder="Select Date"
                />
              </Col>
              <Col md={4}>
                <Flatpickr
                  value={formData.endDate}
                  onChange={(date) => handleTimePartChange(date, 'endDate')}
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: "H:i",
                    time_24hr: true,
                    minuteIncrement: 15,
                  }}
                  className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                  placeholder="Select Time"
                />
              </Col>
            </Row>
            {errors.endDate && (
              <div className="d-block invalid-feedback">
                {errors.endDate}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{intl.formatMessage({id: 'CALENDAR.DATE.PICKER.DESCRIPTION.FIELD'})}</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={intl.formatMessage({id: 'CALENDAR.DATE.PICKER.DESCRIPTION.FIELD.PLACEHOLDER'})}
              rows={4}
              required
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onHide}>
              {intl.formatMessage({id: 'CALENDAR.DATE.PICKER.CANCEL.BUTTON'})}
            </Button>
            <Button variant="primary" type="submit">
              {intl.formatMessage({id: 'CALENDAR.DATE.PICKER.SAVE.BUTTON'})}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CalendarDatePickerComponent;
